import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled, { css } from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { AssetPickerField } from '../fields/widgets/asset-picker-field';
import { focalPointField } from '../fields/widgets/focal-point-field';
import { altTextField, resolveAltText } from '../fields/widgets/alt-text-field';
import type { AltTextValue } from '../fields/widgets/alt-text-field';
import { selectField } from '../fields/widgets/select-field';
import { urlField } from '../fields/widgets/url-field';
import {
  BODY_LINE_HEIGHT,
  BODY_TEXT_SIZES,
  FONT_ROLES,
  IMAGE_ASPECT_RATIOS,
  lookup,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { ImageAspectRatio, ImageFocalPoint, ImageMobileBehaviour, ImageRadius, ImageSize } from '../fields/tokens';
import { interpolateFromPuck } from '../interpolation';
import { buildImgixDelivery, isImgixHostedSrc } from '../image-src';
import { resolveImageStyle, imgixSizing } from './image-style';

/**
 * Returns false for src values whose scheme browsers could interpret dangerously
 * (javascript:, vbscript:, data: non-image), true for everything else.
 *
 * `sanitizeUrl` cannot be used here because it rewrites `data:image/png;base64,...` (a legitimate
 * base64 image URI) to `about:blank`, breaking the renderer's byte-identical passthrough guarantee
 * for that shape. This narrower check blocks only the actually-dangerous schemes while leaving
 * every legitimate shape untouched: relative paths, protocol-relative URLs, https://, http://,
 * and data:image/* all pass through unchanged.
 *
 * Leading ASCII whitespace is stripped and the result is lowercased before scheme detection,
 * mirroring how browsers normalise URLs before parsing the scheme (WHATWG URL spec §5.1).
 */
function isSafeSrc(src: string): boolean {
  // Strip leading ASCII whitespace (tab, LF, FF, CR, space) and lowercase — matches the
  // browser's own pre-parse normalisation step.
  const normalized = src.replace(/^[\t\n\f\r ]+/, '').toLowerCase();
  // Block script-execution schemes via regex — using a regex rather than a string constant
  // avoids the no-script-url lint rule, which scans string literals for those scheme prefixes.
  if (/^(javascript|vbscript):/.test(normalized)) return false;
  // Block data: URIs that are not image types (e.g. data:text/html, data:application/javascript).
  // data:image/* (png, gif, jpeg, webp, svg+xml, ...) are legitimate and must pass through.
  if (normalized.startsWith('data:') && !normalized.startsWith('data:image/')) return false;
  return true;
}

export type AltText = AltTextValue;

const OBJECT_FIT_OPTIONS = [
  { label: 'Fill the frame (may crop)', value: 'cover' },
  { label: 'Fit inside (may leave space)', value: 'contain' },
] as const;

const SIZE_OPTIONS: { label: string; value: ImageSize }[] = [
  { label: 'Fit content', value: 'fit' },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
  { label: 'Full width', value: 'full' },
];

const ASPECT_RATIO_OPTIONS: { label: string; value: ImageAspectRatio }[] = [
  { label: 'Original', value: 'original' },
  { label: 'Square', value: 'square' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Portrait (3:4)', value: 'portrait' },
];

// Labels disclose shape hazards: Circle forces a square aspect ratio (see the isCircle coupling
// in resolveImageStyle); Pill and Circle render identically on square images because both radii
// clamp to a full semicircle on the shorter side.
const RADIUS_OPTIONS: { label: string; value: ImageRadius }[] = [
  { label: 'None', value: 'none' },
  { label: 'Slightly rounded', value: 'subtle' },
  { label: 'Rounded', value: 'rounded' },
  { label: 'Pill (best on wide images; looks the same as Circle on a square image)', value: 'pill' },
  { label: 'Circle (crops to a square; looks the same as Pill on a square image)', value: 'circle' },
];

// The Image block's "On mobile" control, matching FlexLayout's `mobileStack` naming/shape
// convention (see `tokens.ts`'s `IMAGE_MOBILE_BEHAVIOURS` doc comment).
const MOBILE_OPTIONS: { label: string; value: ImageMobileBehaviour }[] = [
  { label: 'Stretch to fill width', value: 'full' },
  { label: 'Keep chosen size', value: 'keep' },
  { label: 'Hide on mobile', value: 'hide' },
];

// Caption alignment: matches paragraph.tsx/link.tsx `align` control (same value set, same
// `radio` field type). `radio` fields need a manual `ai.schema`; `selectField()` only bakes
// schema for `custom` fields.
const CAPTION_ALIGN_OPTIONS: { label: string; value: CaptionAlign }[] = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

type CaptionAlign = 'center' | 'left' | 'right';

// Validated via lookup() so an unrecognised value falls back to 'left' rather than reaching the
// DOM as an arbitrary CSS custom property value.
const CAPTION_ALIGNS: Record<string, CaptionAlign> = {
  left: 'left',
  center: 'center',
  right: 'right',
};

// `eager` stops the browser's native lazy-loading from deferring the fetch. The published page
// renders client-side (dynamic, ssr:false), so `eager`/fetchpriority cannot improve first paint
// or LCP -- it only affects fetch timing once the client-rendered page exists.
type ImageLoading = 'eager' | 'lazy';

const LOADING_OPTIONS: { label: string; value: ImageLoading }[] = [
  { label: 'Lazy (default) — waits until scrolled near the viewport', value: 'lazy' },
  { label: 'Load immediately once the page has rendered', value: 'eager' },
];

const LINK_NEW_TAB_OPTIONS = [
  { label: 'No', value: 'no' },
  { label: 'Yes', value: 'yes' },
] as const;

export type ImageProps = {
  src: string;
  alt: AltText;
  /** New key. Empty string means "no link" -- no wrapper is rendered in that case, in either editor or production. */
  linkHref?: string;
  linkOpenInNewTab: 'no' | 'yes';
  /**
   * A `IMAGE_SIZES` key, or a raw CSS width carried over from before this was a size-preset
   * control (kept-key collision: `width` kept its key but changed from a free-text width to a
   * preset scale).
   */
  width: string;
  aspectRatio: ImageAspectRatio;
  /** Literal CSS `object-fit` value. Kept-key: 'fill'/'none' saved on old pages pass through verbatim. */
  objectFit: string;
  focalPoint: ImageFocalPoint;
  /**
   * An `IMAGE_RADII` key, or a raw CSS length from before this was a preset scale
   * (kept-key: `borderRadius` kept its key but changed from free-text to a preset scale).
   */
  borderRadius: string;
  caption?: string;
  captionAlign: CaptionAlign;
  loading: ImageLoading;
  mobile: ImageMobileBehaviour;
};

/** Placeholder shown in the editor before an image URL has been set. */
export function ImagePlaceholder({
  width = '100%',
  aspectRatio = IMAGE_ASPECT_RATIOS.original,
  borderRadius,
}: {
  width?: string;
  aspectRatio?: string;
  borderRadius?: string;
}): JSX.Element {
  // 'auto' (the Original preset) has no intrinsic size to fall back on before an image URL is
  // set, so give the empty state an explicit height in that case; any real ratio can size itself.
  const isAutoRatio = aspectRatio === 'auto';

  return (
    <div
      style={{
        width,
        aspectRatio: isAutoRatio ? undefined : aspectRatio,
        height: isAutoRatio ? 200 : undefined,
        background: 'var(--dcm-cms-image-placeholder-bg, #f0f2f4)',
        borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dcm-cms-image-placeholder-color, #646d72)',
        fontSize: 14,
      }}
    >
      No image URL set
    </div>
  );
}

/**
 * Mobile visibility rule for 'hide'. Applied to {@link StyledImage} when there is no caption, or
 * to {@link StyledFigure} when there is one (hides image and caption together with one rule).
 * Set to `display: none` in production only -- left unset in the editor so the block stays
 * visible/selectable; see render's `isEditing` branch.
 *
 * This is mobile *visibility*; mobile *width* ('full'/'keep', `--cms-image-mobile-width`) is a
 * separate concern that only ever applies to {@link StyledImage}.
 */
const mobileHideOnHideRule = css`
  @media ${mediaQueries.largePhone} {
    display: var(--cms-image-mobile-display, block);
  }
`;

// `fetchpriority` is not in styled-components' fixed HTML-attribute allowlist (added to the HTML
// spec after that list was frozen), so it gets silently dropped without this override. The
// shouldForwardProp narrows to exactly `fetchpriority` so the override doesn't disable filtering
// for any other attribute.
const StyledImage = styled.img.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) => prop === 'fetchpriority' || defaultValidatorFn(prop),
})`
  display: block;
  width: var(--cms-image-width, 100%);
  aspect-ratio: var(--cms-image-aspect-ratio, auto);
  object-fit: var(--cms-image-object-fit, cover);
  object-position: var(--cms-image-focal-point, center center);
  border-radius: var(--cms-image-border-radius, 0);

  @media ${mediaQueries.largePhone} {
    /* Falls back to the desktop width when 'keep' -- see spacer.tsx's mobileHeight for the same
       fallback-chain trick, so 'keep' has no second value to keep in sync. Mobile WIDTH is only
       ever set here, never on StyledFigure -- it governs the image's own box, not the block's
       visibility (see mobileHideOnHideRule above for that separate concern). */
    width: var(--cms-image-mobile-width, var(--cms-image-width, 100%));
  }

  ${mobileHideOnHideRule}
`;

/**
 * Outermost wrapper for the rendered block whenever there's a caption to hide alongside the
 * image -- see `mobileHideOnHideRule` above for why hiding THIS element (rather than duplicating
 * the same rule onto {@link StyledImage} and {@link StyledCaption} individually) is what makes
 * 'Hide on mobile' actually drop the whole block, caption included, instead of leaving a dangling
 * caption with no image above it on phones.
 */
const StyledFigure = styled.figure`
  margin: 0;

  ${mobileHideOnHideRule}
`;

/** Caption typography matches paragraph.tsx's theme-token approach: no per-instance override
 * controls are offered for it, so the tokens are the only fallback layer (no `--cms-image-*`
 * override property needed, since there is nothing an author can set to override it). */
const StyledCaption = styled.figcaption`
  margin: var(--dcm-cms-image-caption-margin, 8px 0 0);
  color: var(--dcm-cms-image-caption-color, ${TEXT_COLOR_ROLES.muted});
  font-family: var(--dcm-cms-image-caption-font-family, ${FONT_ROLES.body});
  font-size: var(--dcm-cms-image-caption-font-size, ${BODY_TEXT_SIZES.sm});
  line-height: var(--dcm-cms-image-caption-line-height, ${BODY_LINE_HEIGHT});
  /* Per-instance override, unlike the theme tokens above -- captionAlign is an author choice,
     not a merchant theme setting, so it goes through a --cms-* (not --dcm-*) property. */
  text-align: var(--cms-image-caption-align, left);
`;

type ImageLinkProps = {
  href: string;
  openInNewTab: 'no' | 'yes';
  isEditing: boolean;
  children: React.ReactNode;
  /** Accessible name for the link when the image inside it contributes no accessible name (e.g.
   * decorative image or empty alt). Only applied to the production `<a>`; the editor `<span>`
   * branch is left alone -- Puck's own selection/drag UI conveys identity there. */
  ariaLabel?: string;
};

/**
 * Wraps the image in a real `<a>` in production, following `link.tsx`'s URL conventions
 * (`sanitizeUrl`, `rel="noopener noreferrer"` only when opening in a new tab).
 *
 * In the Puck editor, an `<a>` around a draggable/selectable block swallows Puck's own
 * drag/selection pointer events (cms/AGENTS.md gotcha #3). A non-interactive `<span>` stands in
 * for the link in the editor; the real `<a>` takes over in production.
 *
 * Renders no wrapper at all, in either mode, when there is no link.
 */
function ImageLink({ href, openInNewTab, isEditing, children, ariaLabel }: ImageLinkProps): JSX.Element {
  if (!href) return <>{children}</>;

  if (isEditing) {
    // Editor: plain, non-interactive element so Puck's own drag/selection handling on the image
    // underneath is not swallowed by a nested interactive <a>.
    return <span style={{ display: 'block' }}>{children}</span>;
  }

  const newTab = openInNewTab === 'yes';
  return (
    <a
      href={sanitizeUrl(href)}
      target={newTab ? '_blank' : undefined}
      // Without noopener the destination gets a window.opener handle back into the storefront.
      rel={newTab ? 'noopener noreferrer' : undefined}
      style={{ display: 'block' }}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

/** Image block: a URL plus link, sizing, cropping, and captioning controls. */
export const imageConfig: ComponentConfig<ImageProps> = {
  label: 'Image',
  ai: {
    instructions:
      'Displays an image from a URL. Use for hero images, illustrations, and media. Always provide alt text for accessibility, or mark the image decorative if it is purely visual.',
  },
  // Field order IS the panel layout (Puck renders fields in config order).
  fields: {
    src: {
      type: 'custom',
      label: 'Image',
      render: AssetPickerField,
      ai: {
        schema: { type: 'string' },
        stream: false,
        instructions: 'Full image URL. Do not stream partial URLs.',
      },
    },
    alt: altTextField(
      'Alt text',
      'Descriptive alt text for screen readers. Mark the image decorative instead if it conveys no information of its own (e.g. a background flourish).'
    ),
    width: selectField(
      'Size',
      SIZE_OPTIONS,
      "Preset image width. 'Fit content' shrinks to the image's own size; 'Full width' fills the column. 'Small'/'Medium'/'Large' use the Image block's own size scale."
    ),
    aspectRatio: selectField(
      'Aspect ratio',
      ASPECT_RATIO_OPTIONS,
      "'Original' uses the image's own proportions, uncropped. The other presets impose a fixed shape, cropping according to the Crop and Focal point controls below. Overridden to a square by Corners' 'Circle' option, regardless of what's chosen here.",
      "Overridden to a square if Corners is set to 'Circle', regardless of what you pick here."
    ),
    objectFit: selectField(
      'Crop',
      OBJECT_FIT_OPTIONS,
      "Only meaningful when Aspect ratio is not 'Original'. 'Fill the frame' crops the image to cover the shape completely; 'Fit inside' shrinks the image so all of it stays visible, which can leave empty space."
    ),
    focalPoint: focalPointField(
      'Focal point',
      'Which part of the image to keep visible when cropping. Only meaningful when Crop is set to fill the frame.',
      "Only has an effect when Crop is set to 'Fill the frame (may crop)'."
    ),
    borderRadius: selectField(
      'Corners',
      RADIUS_OPTIONS,
      "Corner treatment. 'Pill' and 'Circle' are shape presets relative to the image's own box, not a themed radius -- 'Circle' also forces a square aspect ratio so it renders as an actual circle. On a square image, 'Pill' and 'Circle' render identically. 'Slightly rounded' and 'Rounded' use this block's own image-scaled radius tokens.",
      "On a square image, 'Pill' and 'Circle' look identical -- both round every corner into a full half-circle."
    ),
    caption: { type: 'text', label: 'Caption' },
    captionAlign: {
      type: 'radio',
      label: 'Caption alignment',
      options: [...CAPTION_ALIGN_OPTIONS],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    // Empty `linkHref` means "no link" -- `urlField()` adds a placeholder and hint for authors.
    linkHref: urlField('Link URL', {
      placeholder: 'https://example.com/page',
      hint: 'Leave empty for no link.',
      instructions: 'Full URL or relative path to make the whole image a link. Leave empty for no link.',
    }),
    linkOpenInNewTab: {
      type: 'radio',
      label: 'Open in new tab',
      options: [...LINK_NEW_TAB_OPTIONS],
      ai: { schema: { type: 'string', enum: ['no', 'yes'] } },
    },
    mobile: selectField(
      'On mobile',
      MOBILE_OPTIONS,
      "'Stretch to fill width' makes the image fill its column on phones regardless of the chosen Size -- almost always correct, since a fixed desktop Size can otherwise leave awkward whitespace or force a squeeze on a narrow screen. 'Keep chosen size' preserves the desktop Size at every breakpoint. 'Hide on mobile' removes the whole block -- image and caption -- on phones (e.g. a desktop-only banner); it stays visible, de-emphasised, in the editor so it's still selectable.",
      "'Hide on mobile' removes the whole block -- image and caption -- on phones. If a Link URL is set, its destination becomes unreachable there too."
    ),
    loading: {
      type: 'radio',
      label: 'Loading',
      options: [...LOADING_OPTIONS],
      ai: {
        schema: { type: 'string', enum: ['lazy', 'eager'] },
        instructions:
          "Set to 'eager' for an image that should start loading as soon as the page has rendered, instead of waiting until it scrolls near the viewport. This page renders client-side, so this control cannot improve first paint or Largest Contentful Paint. Leave 'lazy' for images below the fold.",
      },
    },
  },
  defaultProps: {
    src: '',
    alt: { alt: '', decorative: false },
    width: 'full',
    aspectRatio: 'original',
    objectFit: 'cover',
    focalPoint: 'center',
    borderRadius: 'none',
    caption: '',
    captionAlign: 'left',
    linkHref: '',
    linkOpenInNewTab: 'no',
    mobile: 'full',
    loading: 'lazy',
  },
  render: (rawProps) => {
    const { alt: rawAlt, linkOpenInNewTab = 'no', loading = 'lazy', puck } = rawProps;

    // Saved page JSON is unvalidated and there is no error boundary in the published `Render`
    // path. Coerce every string prop at the top of render: a non-string value (object, number,
    // array, null) is treated as absent (coerced to '') so downstream branches handle it safely.
    // `fallback` applies only when the key is genuinely `undefined` (absent prop), mirroring
    // destructuring defaults so defaultProps/render-default parity is preserved.
    const asString = (value: unknown, fallback = ''): string => {
      if (value === undefined) return fallback;
      return typeof value === 'string' ? value : '';
    };

    const src = asString(rawProps.src);
    const linkHref = asString(rawProps.linkHref);
    const caption = asString(rawProps.caption);
    const width = asString(rawProps.width, 'full');
    const objectFit = asString(rawProps.objectFit, 'cover');
    const borderRadius = asString(rawProps.borderRadius, 'none');
    const aspectRatio = asString(rawProps.aspectRatio, 'original');
    const focalPoint = asString(rawProps.focalPoint, 'center');
    const mobile = asString(rawProps.mobile, 'full');
    const captionAlign = lookup<CaptionAlign>(CAPTION_ALIGNS, asString(rawProps.captionAlign, 'left'), 'left');

    const resolvedAlt = resolveAltText(rawAlt);
    // Trim at the single point the alt attribute is produced; whitespace-only alt is effectively empty.
    const altAttribute = resolvedAlt.decorative ? '' : resolvedAlt.alt.trim();

    // Computed unconditionally above the early `!src` return so it can gate that return.
    // `puck` is typed as always-present by @puckeditor/core, but some test harnesses render
    // this component's `render` directly without supplying it -- guard defensively.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const isEditing = Boolean(puck?.isEditing);

    const resolved = resolveImageStyle({ width, aspectRatio, objectFit, focalPoint, borderRadius, mobile });

    if (!src || !isSafeSrc(src)) {
      // Only show the placeholder in the editor; production renders nothing for a missing/unsafe src.
      // An empty fragment satisfies the ReactElement return type without emitting a DOM node.
      if (!isEditing) return <></>;

      return (
        <ImagePlaceholder
          // An empty width means "unset", so fall back to the placeholder's own default.
          width={width === '' ? undefined : resolved.width}
          aspectRatio={resolved.aspectRatio}
          borderRadius={resolved.borderRadius}
        />
      );
    }

    const isEager = loading === 'eager';

    // 'hide' sets display:none at the mobile breakpoint in production only. In the editor the
    // block stays visible/selectable; reduced opacity is applied instead (below). WHERE this var
    // lands -- on the image or the figure -- depends on whether there is a caption to hide with it.
    const hideOnMobileStyle: React.CSSProperties =
      resolved.mobileHide && !isEditing ? ({ '--cms-image-mobile-display': 'none' } as React.CSSProperties) : {};

    const imageStyle = {
      '--cms-image-width': resolved.width,
      '--cms-image-aspect-ratio': resolved.aspectRatio,
      '--cms-image-object-fit': resolved.objectFit,
      '--cms-image-focal-point': resolved.focalPoint,
      '--cms-image-border-radius': resolved.borderRadius,
      // 'keep' leaves --cms-image-mobile-width unset so the stylesheet's fallback chain
      // (var(--cms-image-mobile-width, var(--cms-image-width, 100%))) resolves to the desktop
      // width -- the mobile-width fallback-chain trick, same as spacer.tsx's mobileHeight 'same'.
      ...(resolved.mobileWidth ? { '--cms-image-mobile-width': resolved.mobileWidth } : {}),
      // caption.trim() matches the figure branch below: a whitespace-only caption is "no caption"
      // in both places so the hide-var placement can never disagree between the two branches.
      ...(caption.trim() ? {} : hideOnMobileStyle),
    } as React.CSSProperties;

    // Imgix-hosted sources are delivered with optimised src/srcSet/sizes. Non-Imgix sources
    // (relative paths, data: URIs, third-party URLs, raw S3) pass through untouched.
    const imgixDelivery = isImgixHostedSrc(src) ? buildImgixDelivery(src, imgixSizing(width)) : undefined;

    // Lowercase `fetchpriority` (not camelCase `fetchPriority`): react-dom 18.3.1 does not
    // special-case that prop name yet, so the camelCase spelling would land on the DOM as the
    // attribute `fetchPriority` (invalid HTML) with a console warning. Typed as an intersection
    // because `fetchpriority` is not part of `React.ImgHTMLAttributes`.
    const imgAttributes: React.ImgHTMLAttributes<HTMLImageElement> & { fetchpriority?: 'high' } = {
      src: imgixDelivery?.src ?? src,
      ...(imgixDelivery
        ? { srcSet: imgixDelivery.srcSet, ...(imgixDelivery.sizes !== undefined ? { sizes: imgixDelivery.sizes } : {}) }
        : {}),
      alt: altAttribute,
      loading: isEager ? 'eager' : 'lazy',
      style: {
        ...imageStyle,
        // Editor-only de-emphasis for 'hide' -- see the CSS var comment above for why production
        // uses `display: none` at the breakpoint instead of this.
        ...(resolved.mobileHide && isEditing ? { opacity: 0.4 } : {}),
      },
      ...(isEager ? { fetchpriority: 'high' } : {}),
    };

    const image = <StyledImage {...imgAttributes} />;

    // WCAG 2.4.4 / 4.1.2: a link must have an accessible name. When the image contributes none
    // (decorative or empty/whitespace alt), use the trimmed caption as the fallback. The URL is
    // deliberately never used -- a raw href is not an accessible name. When altAttribute is
    // non-empty the image already names the link naturally; the isEditing branch emits a <span>
    // so no aria-label is needed there.
    function getLinkAriaLabel(): string | undefined {
      if (!linkHref || altAttribute !== '') return undefined;
      const trimmedCaption = caption.trim();
      return trimmedCaption !== '' ? trimmedCaption : undefined;
    }
    const linkAriaLabel = getLinkAriaLabel();

    // Editor-only warning when a linked image has no accessible name (no alt and no caption).
    // Only fires when decorative/empty-alt meets a live link; a decorative image with no link is
    // a valid authoring choice and must not warn.
    const showLinkedEmptyAltWarning = isEditing && Boolean(linkHref) && altAttribute === '' && !caption.trim();

    const linkedImage = (
      <ImageLink href={linkHref} openInNewTab={linkOpenInNewTab} isEditing={isEditing} ariaLabel={linkAriaLabel}>
        {image}
      </ImageLink>
    );

    // A whitespace-only caption produces no aria-label (getLinkAriaLabel trims above) so it
    // must also produce no figcaption; trim before deciding.
    if (!caption.trim()) {
      if (!showLinkedEmptyAltWarning) return linkedImage;
      // Editor-only fragment: the warning renders below the linked image without affecting
      // production layout. `showLinkedEmptyAltWarning` is always false in production.
      return (
        <>
          {linkedImage}
          <p data-testid='cms-image-linked-empty-alt-warning' style={{ marginTop: 6, fontSize: 12, color: '#a15c00' }}>
            This image links somewhere but has no alt text. Screen reader users will hear an unnamed link. Add alt text
            describing the link destination, or add a caption (it becomes the link&apos;s accessible name).
          </p>
        </>
      );
    }

    // A decorative image is invisible to the accessibility tree, so <figure>/<figcaption>
    // would caption nothing the user can perceive -- use <div>/<p> via styled-components' `as`.
    const wrapperTag = resolvedAlt.decorative ? 'div' : 'figure';
    const captionTag = resolvedAlt.decorative ? 'p' : 'figcaption';

    // Caption supports {{token}} interpolation like Heading/Paragraph.
    const resolvedCaption = interpolateFromPuck(caption, puck);

    return (
      // The hide-on-mobile var goes on the figure so it hides image and caption together.
      <StyledFigure as={wrapperTag} style={hideOnMobileStyle}>
        {linkedImage}
        <StyledCaption as={captionTag} style={{ '--cms-image-caption-align': captionAlign } as React.CSSProperties}>
          {resolvedCaption}
        </StyledCaption>
      </StyledFigure>
    );
  },
};
